import axios from "axios";
import { authStorage } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

// ── Auth header on every request ───────────────────────────────
api.interceptors.request.use((config) => {
  const token = authStorage.getAccess();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── On 401: try refresh once, then redirect to /login ──────────
let isRefreshing = false;
let refreshQueue = [];

const flushQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";

    // Don't try to refresh on the auth endpoints themselves
    if (status !== 401 || original._retry || url.includes("/auth/")) {
      return Promise.reject(error);
    }

    const refresh = authStorage.getRefresh();
    if (!refresh) {
      authStorage.clear();
      if (typeof window !== "undefined") window.location.assign("/login");
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      })
        .then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        })
        .catch((e) => Promise.reject(e));
    }

    original._retry = true;
    isRefreshing = true;
    try {
      const r = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
      authStorage.setTokens(r.data);
      flushQueue(null, r.data.access_token);
      original.headers.Authorization = `Bearer ${r.data.access_token}`;
      return api(original);
    } catch (e) {
      flushQueue(e, null);
      authStorage.clear();
      if (typeof window !== "undefined") window.location.assign("/login");
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }
);

export const fmtINR = (val) => {
  if (val == null) return "—";
  const n = Number(val);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (Math.abs(n) >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};

export const fmtNum = (n) => {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN");
};

export const timeAgo = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const statusColor = (s) => {
  switch (s) {
    case "active": return "green";
    case "idle": return "amber";
    case "alert": case "critical": return "red";
    case "maintenance": return "slate";
    case "warning": return "amber";
    default: return "slate";
  }
};
